import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { analyticsAPI } from '../../services/api';
import CallPlaybackModal from '../../components/CallPlaybackModal';
import {
  Phone, PhoneIncoming, PhoneOutgoing, Clock, TrendingUp, TrendingDown,
  Users, Activity, Calendar, BarChart3, PieChart, ArrowUpRight,
  ArrowDownRight, Minus, RefreshCw, ChevronDown, PlayCircle, MessageSquare,
  Headphones
} from 'lucide-react';

export default function AnalyticsDashboardPage() {
  const { clientUser } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [overview, setOverview] = useState(null);
  const [callOutcomes, setCallOutcomes] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [error, setError] = useState(null);
  const [selectedCallId, setSelectedCallId] = useState(null);

  const clientId = clientUser?.id;

  const fetchAnalytics = async () => {
    if (!clientId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [overviewRes, outcomesRes, campaignsRes, callsRes] = await Promise.all([
        analyticsAPI.getOverview(clientId, { days }),
        analyticsAPI.getCallOutcomes(clientId, { days }),
        analyticsAPI.getCampaignPerformance(clientId),
        analyticsAPI.getCallsWithRecordings(clientId, { days, limit: 20 })
      ]);

      setOverview(overviewRes.data);
      setCallOutcomes(outcomesRes.data);
      setCampaigns(campaignsRes.data);
      setRecentCalls(callsRes.data.calls || []);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [clientId, days]);

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const mins = Math.floor(seconds / 60);
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) {
      return `${hrs}h ${mins % 60}m`;
    }
    return `${mins}m`;
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  // Calculate trend (mock for now - would need historical data)
  const getTrend = (current, previous) => {
    if (!previous || previous === 0) return { direction: 'neutral', percent: 0 };
    const change = ((current - previous) / previous) * 100;
    return {
      direction: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral',
      percent: Math.abs(change).toFixed(1)
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white p-6 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-teal-400 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400">{t('analytics.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white p-6">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
          <p className="text-red-400 mb-4">{t('analytics.failedToLoad')}</p>
          <button
            onClick={fetchAnalytics}
            className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg"
          >
            {t('analytics.retry')}
          </button>
        </div>
      </div>
    );
  }

  const metrics = overview?.metrics || {};
  const outcomes = callOutcomes?.outcomes || {};
  const durationDist = callOutcomes?.duration_distribution || {};
  const hourlyDist = callOutcomes?.hourly_distribution || {};

  // Find peak hour
  const peakHour = Object.entries(hourlyDist).reduce((max, [hour, count]) => 
    count > max.count ? { hour, count } : max, { hour: '00', count: 0 }
  );

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white p-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">{t('analytics.title')}</h1>
          <p className="text-gray-400 text-sm">{t('analytics.subtitle')}</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Time Range Selector */}
          <div className="relative">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="appearance-none bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 pr-10 text-white outline-none focus:border-teal-400"
              data-testid="days-selector"
            >
              <option value={7}>{t('analytics.last7Days')}</option>
              <option value={14}>{t('analytics.last14Days')}</option>
              <option value={30}>{t('analytics.last30Days')}</option>
              <option value={90}>{t('analytics.last90Days')}</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
          <button
            onClick={fetchAnalytics}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Total Calls */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4" data-testid="metric-total-calls">
          <div className="flex items-center justify-between mb-2">
            <Phone className="w-5 h-5 text-blue-400" />
            <span className="text-xs text-gray-500">Total</span>
          </div>
          <p className="text-3xl font-bold text-white mb-1">{formatNumber(metrics.total_calls)}</p>
          <p className="text-gray-400 text-sm">{t('analytics.totalCalls')}</p>
        </div>

        {/* Answered Calls */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4" data-testid="metric-answered">
          <div className="flex items-center justify-between mb-2">
            <PhoneIncoming className="w-5 h-5 text-green-400" />
            <span className="text-xs text-green-400">{metrics.answer_rate}%</span>
          </div>
          <p className="text-3xl font-bold text-white mb-1">{formatNumber(metrics.answered_calls)}</p>
          <p className="text-gray-400 text-sm">{t('analytics.answered')}</p>
        </div>

        {/* Avg Duration */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4" data-testid="metric-duration">
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-5 h-5 text-purple-400" />
            <span className="text-xs text-gray-500">Avg</span>
          </div>
          <p className="text-3xl font-bold text-white mb-1">{formatDuration(metrics.avg_duration_seconds)}</p>
          <p className="text-gray-400 text-sm">{t('analytics.avgDuration')}</p>
        </div>

        {/* Total Talk Time */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4" data-testid="metric-talk-time">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-5 h-5 text-teal-400" />
            <span className="text-xs text-gray-500">Total</span>
          </div>
          <p className="text-3xl font-bold text-white mb-1">{formatDuration(metrics.total_duration_seconds)}</p>
          <p className="text-gray-400 text-sm">{t('analytics.talkTime')}</p>
        </div>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800/30 border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <PhoneOutgoing className="w-4 h-4 text-blue-400" />
            <span className="text-gray-400 text-sm">{t('analytics.outbound')}</span>
          </div>
          <p className="text-xl font-semibold">{formatNumber(metrics.outbound_calls)}</p>
        </div>
        <div className="bg-gray-800/30 border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <PhoneIncoming className="w-4 h-4 text-green-400" />
            <span className="text-gray-400 text-sm">Inbound</span>
          </div>
          <p className="text-xl font-semibold">{formatNumber(metrics.inbound_calls)}</p>
        </div>
        <div className="bg-gray-800/30 border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="w-4 h-4 text-orange-400" />
            <span className="text-gray-400 text-sm">Active Campaigns</span>
          </div>
          <p className="text-xl font-semibold">{metrics.active_campaigns || 0}</p>
        </div>
        <div className="bg-gray-800/30 border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-teal-400" />
            <span className="text-gray-400 text-sm">Agents Online</span>
          </div>
          <p className="text-xl font-semibold">{metrics.agents_logged_in || 0}</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Call Outcomes */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-5" data-testid="outcomes-chart">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-purple-400" />
            Call Outcomes
          </h3>
          <div className="space-y-3">
            {Object.entries(outcomes).map(([key, value]) => {
              const total = Object.values(outcomes).reduce((a, b) => a + b, 0);
              const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
              const colors = {
                completed: 'bg-green-500',
                no_answer: 'bg-yellow-500',
                busy: 'bg-orange-500',
                failed: 'bg-red-500',
                voicemail: 'bg-blue-500',
                other: 'bg-gray-500'
              };
              return (
                <div key={key} className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${colors[key] || 'bg-gray-500'}`}></div>
                  <span className="text-gray-400 text-sm capitalize flex-1">{key.replace('_', ' ')}</span>
                  <span className="text-white font-medium">{value}</span>
                  <span className="text-gray-500 text-sm w-12 text-right">{percent}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Duration Distribution */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-5" data-testid="duration-chart">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-teal-400" />
            Call Duration Distribution
          </h3>
          <div className="space-y-3">
            {Object.entries(durationDist).map(([bucket, count]) => {
              const total = Object.values(durationDist).reduce((a, b) => a + b, 0);
              const percent = total > 0 ? (count / total) * 100 : 0;
              return (
                <div key={bucket} className="flex items-center gap-3">
                  <span className="text-gray-400 text-sm w-16">{bucket}</span>
                  <div className="flex-1 h-6 bg-gray-800 rounded-lg overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-teal-500 to-teal-400 rounded-lg transition-all duration-500"
                      style={{ width: `${percent}%` }}
                    ></div>
                  </div>
                  <span className="text-white font-medium w-12 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Hourly Distribution */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-5 mb-6" data-testid="hourly-chart">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-400" />
          Calls by Hour
          <span className="text-sm text-gray-500 font-normal ml-2">
            Peak: {peakHour.hour}:00 ({peakHour.count} calls)
          </span>
        </h3>
        <div className="flex items-end gap-1 h-32">
          {Object.entries(hourlyDist).map(([hour, count]) => {
            const maxCount = Math.max(...Object.values(hourlyDist), 1);
            const heightPercent = (count / maxCount) * 100;
            const isPeak = hour === peakHour.hour;
            return (
              <div key={hour} className="flex-1 flex flex-col items-center gap-1">
                <div 
                  className={`w-full rounded-t transition-all duration-300 ${
                    isPeak ? 'bg-teal-400' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  style={{ height: `${Math.max(heightPercent, 2)}%` }}
                  title={`${hour}:00 - ${count} calls`}
                ></div>
                {parseInt(hour) % 4 === 0 && (
                  <span className="text-xs text-gray-500">{hour}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Daily Breakdown */}
      {overview?.daily_breakdown && overview.daily_breakdown.length > 0 && (
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-5 mb-6" data-testid="daily-chart">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-400" />
            Daily Call Volume
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 border-b border-gray-800">
                  <th className="text-left py-2 px-3">Date</th>
                  <th className="text-right py-2 px-3">Total</th>
                  <th className="text-right py-2 px-3">Answered</th>
                  <th className="text-right py-2 px-3">Rate</th>
                  <th className="text-right py-2 px-3">Duration</th>
                </tr>
              </thead>
              <tbody>
                {overview.daily_breakdown.slice(-10).map((day) => (
                  <tr key={day.date} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                    <td className="py-2 px-3 text-white">{day.date}</td>
                    <td className="py-2 px-3 text-right">{day.total}</td>
                    <td className="py-2 px-3 text-right text-green-400">{day.answered}</td>
                    <td className="py-2 px-3 text-right">
                      {day.total > 0 ? ((day.answered / day.total) * 100).toFixed(0) : 0}%
                    </td>
                    <td className="py-2 px-3 text-right text-gray-400">{formatDuration(day.duration)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Campaign Performance */}
      {campaigns?.campaigns && campaigns.campaigns.length > 0 && (
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-5" data-testid="campaigns-table">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-orange-400" />
            Campaign Performance
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 border-b border-gray-800">
                  <th className="text-left py-2 px-3">Campaign</th>
                  <th className="text-center py-2 px-3">Status</th>
                  <th className="text-right py-2 px-3">Contacts</th>
                  <th className="text-right py-2 px-3">Dialed</th>
                  <th className="text-right py-2 px-3">Answered</th>
                  <th className="text-right py-2 px-3">Answer %</th>
                  <th className="text-right py-2 px-3">Progress</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.campaigns.slice(0, 10).map((campaign) => (
                  <tr key={campaign.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                    <td className="py-2 px-3 text-white font-medium">{campaign.name}</td>
                    <td className="py-2 px-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        campaign.status === 'running' ? 'bg-green-500/20 text-green-400' :
                        campaign.status === 'paused' ? 'bg-yellow-500/20 text-yellow-400' :
                        campaign.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {campaign.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right">{campaign.total_contacts}</td>
                    <td className="py-2 px-3 text-right">{campaign.dialed}</td>
                    <td className="py-2 px-3 text-right text-green-400">{campaign.answered}</td>
                    <td className="py-2 px-3 text-right">{campaign.answer_rate}%</td>
                    <td className="py-2 px-3 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <div className="w-20 h-2 bg-gray-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-teal-400 rounded-full"
                            style={{ width: `${campaign.completion_rate}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-400">{campaign.completion_rate}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Calls with Recordings */}
      {recentCalls && recentCalls.length > 0 && (
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-5 mt-6" data-testid="recent-calls-table">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Headphones className="w-5 h-5 text-purple-400" />
            Recent Calls with Recordings
            <span className="text-sm font-normal text-gray-500 ml-2">
              Click to play recording and view transcript
            </span>
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 border-b border-gray-800">
                  <th className="text-left py-2 px-3">Phone Number</th>
                  <th className="text-center py-2 px-3">Direction</th>
                  <th className="text-center py-2 px-3">Status</th>
                  <th className="text-right py-2 px-3">Duration</th>
                  <th className="text-left py-2 px-3">Agent/Campaign</th>
                  <th className="text-center py-2 px-3">Media</th>
                  <th className="text-left py-2 px-3">Date</th>
                  <th className="text-center py-2 px-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {recentCalls.map((call) => (
                  <tr 
                    key={call.call_id} 
                    className="border-b border-gray-800/50 hover:bg-gray-800/30 cursor-pointer transition-colors"
                    onClick={() => setSelectedCallId(call.call_id)}
                  >
                    <td className="py-2 px-3 text-white font-medium">
                      {call.direction === 'inbound' ? call.from_number : call.to_number}
                    </td>
                    <td className="py-2 px-3 text-center">
                      {call.direction === 'inbound' ? (
                        <span className="inline-flex items-center gap-1 text-green-400">
                          <PhoneIncoming className="w-3 h-3" />
                          In
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-blue-400">
                          <PhoneOutgoing className="w-3 h-3" />
                          Out
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        call.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        call.status === 'answered' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {call.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right text-gray-400">
                      {formatDuration(call.duration_seconds)}
                    </td>
                    <td className="py-2 px-3">
                      {call.campaign_name ? (
                        <span className="text-orange-400 text-xs">{call.campaign_name}</span>
                      ) : call.agent_name ? (
                        <span className="text-teal-400 text-xs">{call.agent_name}</span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {call.has_recording && (
                          <span className="text-purple-400" title="Has Recording">
                            <PlayCircle className="w-4 h-4" />
                          </span>
                        )}
                        {call.has_transcript && (
                          <span className="text-teal-400" title={`${call.message_count} messages`}>
                            <MessageSquare className="w-4 h-4" />
                          </span>
                        )}
                        {!call.has_recording && !call.has_transcript && (
                          <span className="text-gray-600">-</span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-gray-400 text-xs">
                      {call.started_at ? new Date(call.started_at).toLocaleString([], {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : '-'}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCallId(call.call_id);
                        }}
                        className="px-3 py-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-xs transition-colors"
                        data-testid={`play-call-${call.call_id}`}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Call Playback Modal */}
      {selectedCallId && (
        <CallPlaybackModal
          callId={selectedCallId}
          clientId={clientId}
          onClose={() => setSelectedCallId(null)}
        />
      )}
    </div>
  );
}
